import {
    action,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
} from "mobx";
import { isEqual } from "lodash";
import { AcxStore } from "stores/RootStore";
import type { IRootStore } from "stores/RootStore";
import {
    AgentCoaching,
    AgentCoachingStatus,
    targetedConversationIds,
    targetedEvaluationIds,
} from "../../../../models/AgentModels/AgentCoaching";
import {
    AgentCoachingData,
    AgentCoachingService,
    CoachingNoteLockResultStatus,
} from "../../../../services/AgentCoachingService";
import { BaseStore } from "../../../../stores/BaseStore";
import { AgentDashboardStore } from "components/Agents/Dashboard/Stores/AgentDashboardStore";
import { AuthStore } from "stores/AuthStore";
import { AcxAgentService } from "services/AcxAgentService";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";

export const LoadingAICoachingNotes = "Generate Coaching Notes";
const CoachingLockTimeoutMilliseconds = 10_000;

export enum CoachingLockStatus {
    Idle = "Idle",
    Acquiring = "Acquiring",
    Acquired = "Acquired",
    Denied = "Denied",
    AlreadyCoached = "AlreadyCoached",
    Error = "Error",
}

@AcxStore
export class EvalReviewStore extends BaseStore {
    private readonly agentCoachingService: AgentCoachingService =
        new AgentCoachingService();
    private readonly acxAgentService: AcxAgentService = new AcxAgentService();

    @observable focusAreaQuestions?: { value: string }[];

    /**
     * True when the user is editing a drafted review
     */
    @observable isEditing: boolean;
    @observable agentCoaching?: AgentCoaching;
    @observable targetedEvaluations?: AgentCoachingData[];
    @observable moduleScoresForEvaluations?: any[];

    @observable hasGeneratedCoachingNotes: boolean = false;
    @observable isGeneratingCoachingNotesFromKBo: boolean = false;
    @observable isCoached: boolean = false;

    /**
     * Timestamp of the most recent draft save. Kept as its own observable so
     * the "Draft saved on ..." banner updates live on each save (the entity's
     * modifiedOn is not observable). (AUT-10104)
     */
    @observable draftSavedOn?: string;

    /**
     * Coaching-notes lock (AUT-10104). First-come-first-serve: only one coach
     * may edit/save the coaching for a given agent + conversation at a time.
     */
    @observable hasCoachingLock: boolean = false;
    @observable isLockedByOther: boolean = false;
    @observable coachingLockStatus = CoachingLockStatus.Idle;

    private lockedAgentId?: string;
    private lockedCoachingResourceIds?: string[];
    private coachingLockRequest?: Promise<boolean>;
    private coachingLockGeneration = 0;
    private coachingLockFailureReported = false;
    private coachingLockNavigationTarget?: string;

    @observable
    abortController: AbortController = new AbortController();

    constructor(private rootStore: IRootStore) {
        super("Eval Review");

        makeObservable(this);

        this.isEditing = false;

        reaction(
            (r) => ({
                activeLocation: this.rootStore.activeLocation,
            }),
            (arg) => {
                if (
                    arg.activeLocation &&
                    arg.activeLocation.location !==
                        "/app/agentcoaching/evaluationreview"
                ) {
                    return;
                }

                this.setupAsyncTask("Load Evaluation Focus Areas", () =>
                    this.loadFocusAreaQuestions(),
                );
            },
            { fireImmediately: true },
        );
    }

    @computed get coachingConversationIds(): string[] {
        return this.agentCoaching
            ? targetedConversationIds(this.agentCoaching)
            : [];
    }

    @computed get coachingLockFailureMessage(): string {
        switch (this.coachingLockStatus) {
            case CoachingLockStatus.AlreadyCoached:
                return "This coaching has already been completed. Refresh to see the latest status.";
            case CoachingLockStatus.Denied:
                return "Another user is currently coaching this. Please try again later.";
            default:
                return "Unable to acquire the coaching lock. Please try again.";
        }
    }

    /**
     * The conversation being coached. An evaluation target does not carry an
     * audio metadata id of its own, so fall back to the conversation behind the
     * evaluation. Undefined only for Live evaluations, which have no audio.
     */
    @computed get coachingAudioMetadataId(): string | undefined {
        return (
            this.coachingConversationIds[0] ??
            this.getAudioMetadataIdForTarget(this.targetedEvaluations?.[0])
        );
    }

    @computed get isNoEvalCoaching(): boolean {
        if (!this.agentCoaching) return false;

        return (
            this.coachingConversationIds.length > 0 &&
            targetedEvaluationIds(this.agentCoaching).length === 0
        );
    }

    /**
     * Conversation coaching is locked by audio metadata ID. Live evaluations
     * intentionally have no audio metadata, so their single evaluation ID is
     * the stable resource identifier used for the cache lock instead.
     */
    @computed
    get coachingLockResourceIds(): string[] | undefined {
        const evaluationIds = this.agentCoaching
            ? targetedEvaluationIds(this.agentCoaching)
            : [];

        const conversationId = this.agentCoaching
            ? targetedConversationIds(this.agentCoaching)
            : [];

        return [...evaluationIds, ...conversationId];
    }

    private getAudioMetadataIdForTarget(
        target?: Partial<AgentCoachingData> & {
            audioMetadataId?: string;
            interaction?: { audioMetadataId?: string };
        },
    ) {
        return (
            target?.audioMetadataId ??
            target?.interaction?.audioMetadataId ??
            target?.amdId
        );
    }

    private buildAgentCoachingTargets(
        evaluations: AgentCoachingData[],
        fallbackAudioMetadataId?: string,
    ): AgentCoaching["targets"] {
        const targets = evaluations.reduce<
            Array<{ evaluationId?: string; audioMetadataId?: string }>
        >((result, evaluation) => {
            if (!(evaluation as any).isConversationOnly && evaluation.id) {
                result.push({ evaluationId: evaluation.id });
                return result;
            }

            const audioMetadataId =
                this.getAudioMetadataIdForTarget(evaluation);
            if (audioMetadataId) result.push({ audioMetadataId });

            return result;
        }, []);

        if (targets.length === 0 && fallbackAudioMetadataId) {
            targets.push({ audioMetadataId: fallbackAudioMetadataId });
        }

        return targets as AgentCoaching["targets"];
    }

    /**
     * The create/update endpoints still take the legacy `evaluationId` array and
     * single `audioMetadataId`. Derive them from `targets` so there is one source
     * of truth; both fields disappear with the write-path migration.
     */
    private applyLegacyWriteFields(
        coaching: AgentCoaching,
        evaluations: AgentCoachingData[],
    ) {
        coaching.evaluationId = targetedEvaluationIds(coaching);
        // Mirrors the pre-targets behaviour: an evaluation-backed coaching still
        // reported the conversation behind the evaluation here.
        coaching.audioMetadataId =
            targetedConversationIds(coaching)[0] ??
            this.getAudioMetadataIdForTarget(evaluations[0]);
    }

    /**
     * Shared hydration for the two entry points. Returns whether an existing
     * draft was supplied, which each caller turns into its own `isEditing` rule.
     */
    private hydrateAgentCoaching(
        agentId: string,
        evaluations: AgentCoachingData[],
        agentCoachingDraft?: AgentCoaching,
    ): boolean {
        const firstRec = evaluations[0];

        if (!agentCoachingDraft) {
            this.agentCoaching = new AgentCoaching();
            this.agentCoaching.agentId = agentId;
            this.agentCoaching.targets =
                this.buildAgentCoachingTargets(evaluations);
            this.agentCoaching.agentDoWellNotes = firstRec.agentDoWellNotes;
            this.agentCoaching.acknowledged = firstRec.acknowledged;
            this.agentCoaching.focusAreas = firstRec.focusAreas;
            this.agentCoaching.improvementNotes = firstRec.improvementNotes;

            if (firstRec.agentCoachingId) {
                this.agentCoaching.agentCoachingId = firstRec.agentCoachingId;
            }

            this.applyLegacyWriteFields(this.agentCoaching, evaluations);
            return false;
        }

        this.agentCoaching = agentCoachingDraft;
        if (!this.agentCoaching.targets?.length) {
            // A draft saved before targets existed, or one opened straight from a
            // conversation: rebuild from the evaluations driving this review.
            this.agentCoaching.targets = this.buildAgentCoachingTargets(
                evaluations,
                this.getAudioMetadataIdForTarget(firstRec),
            );
        }

        this.applyLegacyWriteFields(this.agentCoaching, evaluations);
        return true;
    }

    /**
     * Back url to use when a user has no history for the tab.
     * This allows the action buttons in agent coaching to properly send the user
     * back when thay are directed there in a new tab by the agent.
     */
    @computed get backUrl(): string | number {
        if (!this.agentCoaching) return -1;

        const authStore = this.rootStore.getStore(AuthStore);
        const orgId = authStore.orgStore.selectedOrganization?.id;
        if (!orgId) return -1;

        const agentStore = this.rootStore.getStore(AgentDashboardStore);
        const agentName = agentStore.agentName;
        if (!agentName) return -1;

        return `/app/agentcoaching/dashboard/${orgId}/${this.agentCoaching.agentId}/${agentName}`;
    }

    @action
    async initializeStore(
        agentId: string,
        evaluations: AgentCoachingData[],
        isCoached: boolean,
        agentCoachingDraft?: AgentCoaching,
    ) {
        this.hasGeneratedCoachingNotes = false;
        this.targetedEvaluations = evaluations;
        this.isCoached = isCoached;
        this.hasCoachingLock = false;
        this.isLockedByOther = false;
        this.coachingLockStatus = CoachingLockStatus.Idle;

        this.isEditing = this.hydrateAgentCoaching(
            agentId,
            evaluations,
            agentCoachingDraft,
        );

        this.draftSavedOn = this.agentCoaching?.modifiedOn;

        this.moduleScoresForEvaluations =
            await this.agentCoachingService.getModuleScoresForEvaluations(
                evaluations.map((val) => val.id),
            );
    }

    @action
    async initializeStoreFromConvos(
        agentId: string,
        evaluations: AgentCoachingData[],
        agentCoachingDraft?: AgentCoaching,
        isCoached?: boolean,
    ) {
        this.hasGeneratedCoachingNotes = false;
        this.targetedEvaluations = evaluations;
        this.isCoached = !!isCoached;
        this.hasCoachingLock = false;
        this.isLockedByOther = false;
        this.coachingLockStatus = CoachingLockStatus.Idle;

        if (!this.focusAreaQuestions || !this.focusAreaQuestions.length) {
            this.loadFocusAreaQuestions();
        }

        const hasDraft = this.hydrateAgentCoaching(
            agentId,
            evaluations,
            agentCoachingDraft,
        );

        // Only treat as editing if it's an existing record with an ID
        this.isEditing =
            hasDraft &&
            (!!agentCoachingDraft!.id || !!agentCoachingDraft!.agentCoachingId);

        this.draftSavedOn = this.agentCoaching?.modifiedOn;
    }

    @action
    async loadSingleEvalAndInitializeStore(
        agentId: string,
        agentName: string,
        evaluationId: string,
        evaluationNumber: string,
    ) {
        this.setupAsyncTask("Load Single Evaluation Review", async () => {
            await this.initializeStore(
                agentId,
                [
                    {
                        id: evaluationId,
                        evaluationQbId: Number(evaluationNumber),
                    },
                ],
                false,
            );

            this.rootStore.getStore(AgentDashboardStore).agentName = agentName;
        });
    }

    @action
    async loadFocusAreaQuestions() {
        const res = await this.agentCoachingService.getEvaluationQuestions();
        this.focusAreaQuestions = res.map((q) => ({
            value: q.questionText,
        }));
    }

    @action
    async generateCoachingNotes() {
        this.abortController = new AbortController();
        this.setupAsyncTask(LoadingAICoachingNotes, async () => {
            // Notes are generated per conversation; use the one being coached.
            const conversationId = this.coachingAudioMetadataId;
            if (!this.agentCoaching || !conversationId) return;

            try {
                const result = await this.acxAgentService.generateCoachingNotes(
                    this.agentCoaching.agentId,
                    conversationId,
                    this.abortController.signal,
                );

                if (result.successful) {
                    const strengths = result.response.strengths ?? [];
                    const opportunities = result.response.opportunities ?? [];

                    // Format strengths into a bulleted list
                    const strengthsText =
                        strengths.length > 0
                            ? strengths
                                  .map((strength) => `• ${strength}`)
                                  .join("\n")
                            : "No strengths were identified for this conversation.";

                    // Format opportunities with feedback and action items
                    const opportunitiesText =
                        opportunities.length > 0
                            ? opportunities
                                  .map((opp, idx) => {
                                      return (
                                          `**Opportunity ${idx + 1}:**\n` +
                                          `Feedback: ${opp.feedback}\n` +
                                          `Action: ${opp.actionItem}`
                                      );
                                  })
                                  .join("\n\n")
                            : "No opportunities were identified for this conversation.";

                    this.agentCoaching.setDoWellNotes(strengthsText);
                    this.agentCoaching.setImprovementNotes(opportunitiesText);

                    runInAction(() => {
                        this.hasGeneratedCoachingNotes = true;
                    });
                } else {
                    this.rootStore
                        .getStore(MessageStore)
                        .logError(result.message);
                }
            } catch (err) {
                if (this.abortController.signal.aborted) return;
                this.rootStore
                    .getStore(MessageStore)
                    .logError(
                        "Failed to generate coaching notes. Please try again.",
                    );
            }
        });
    }

    @action
    async cancelCoachingNotes() {
        this.abortController.abort();
    }

    @action
    async createAgentCoaching(asDraft?: boolean) {
        if (!this.agentCoaching) {
            console.warn("unable to create agent coaching");
            return true;
        }
        try {
            if (asDraft) {
                const res =
                    await this.agentCoachingService.createCoachingAsDraft(
                        this.agentCoaching,
                    );
                // Remember the newly created draft so subsequent saves update
                // it in place (PUT) instead of creating duplicate drafts.
                if (res && this.agentCoaching) {
                    this.agentCoaching.id = res.id;
                    this.agentCoaching.modifiedOn = res.modifiedOn;
                    this.draftSavedOn = res.modifiedOn;
                    this.isEditing = true;
                }
                return !!res;
            } else {
                await this.agentCoachingService.createCoachingAsCompleted(
                    this.agentCoaching,
                );

                return true;
            }
        } catch (e) {
            return false;
        }
    }

    @action
    async updateAgentCoaching(completed?: boolean) {
        if (!this.agentCoaching) {
            console.warn("unable to create agent coaching");
            return true;
        }
        try {
            if (completed) {
                this.agentCoaching.coachingStatus =
                    AgentCoachingStatus.Completed;
                this.isEditing = false;
            } else
                this.agentCoaching.coachingStatus =
                    AgentCoachingStatus.InProgress;

            const res = await this.agentCoachingService.putCoachingDraft(
                this.agentCoaching,
            );
            if (res && this.agentCoaching) {
                this.agentCoaching.modifiedOn = res.modifiedOn;
                this.draftSavedOn = res.modifiedOn;
            }
            return true;
        } catch (e) {
            return false;
        }
    }

    @action
    async createAgentCoachingNew(asDraft?: boolean) {
        try {
            if (asDraft) {
                const res =
                    await this.agentCoachingService.createCoachingAsDraft(
                        this.agentCoaching!,
                    );
                // Remember the newly created draft so subsequent saves update
                // it in place (PUT) instead of creating duplicate drafts.
                if (res && this.agentCoaching) {
                    this.agentCoaching.id = res.id;
                    this.agentCoaching.modifiedOn = res.modifiedOn;
                    this.draftSavedOn = res.modifiedOn;
                    this.isEditing = true;
                }
                return res;
            } else {
                const res =
                    await this.agentCoachingService.createCoachingAsCompleted(
                        this.agentCoaching!,
                    );

                return res;
            }
        } catch (e) {
            console.log("unable to create");
        }
    }

    @action
    async updateAgentCoachingNew(completed?: boolean) {
        try {
            if (completed) {
                this.agentCoaching!.coachingStatus =
                    AgentCoachingStatus.Completed;
                this.isEditing = false;
            } else
                this.agentCoaching!.coachingStatus =
                    AgentCoachingStatus.InProgress;

            const res = await this.agentCoachingService.putCoachingDraft(
                this.agentCoaching!,
            );
            if (res && this.agentCoaching) {
                this.agentCoaching.modifiedOn = res.modifiedOn;
                this.draftSavedOn = res.modifiedOn;
            }
            return res;
        } catch (e) {
            console.error("Failed to update agent coaching");
        }
    }

    /**
     * Try to acquire the coaching-notes lock session for the current coaching
     * target set. Returns true if this coach may edit. Mirrors the Data
     * Labeling lock: if another coach holds any overlapping target we block
     * editing.
     */
    @action
    async acquireCoachingLock(): Promise<boolean> {
        if (this.hasCoachingLock) {
            this.coachingLockStatus = CoachingLockStatus.Acquired;
            return true;
        }

        if (this.coachingLockRequest) return this.coachingLockRequest;

        const agentId = this.agentCoaching?.agentId;
        const coachingResourceIds = this.coachingLockResourceIds;

        if (!agentId || !coachingResourceIds?.length) {
            this.hasCoachingLock = false;
            this.isLockedByOther = false;
            this.coachingLockStatus = CoachingLockStatus.Error;
            return false;
        }

        const requestGeneration = ++this.coachingLockGeneration;
        this.coachingLockFailureReported = false;
        this.coachingLockStatus = CoachingLockStatus.Acquiring;
        this.isLockedByOther = false;

        let timedOut = false;
        let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

        const lockRequest = this.agentCoachingService
            .tryGetCoachingNoteLock(agentId, coachingResourceIds)
            .then(async (lockStatus) => {
                const acquired =
                    lockStatus === CoachingNoteLockResultStatus.Acquired;

                if (
                    timedOut ||
                    requestGeneration !== this.coachingLockGeneration
                ) {
                    if (
                        acquired &&
                        !(
                            this.hasCoachingLock &&
                            this.lockedAgentId === agentId &&
                            isEqual(
                                [
                                    ...(this.lockedCoachingResourceIds ?? []),
                                ].sort(),
                                [...coachingResourceIds].sort(),
                            )
                        )
                    ) {
                        try {
                            await this.agentCoachingService.releaseCoachingNoteLock(
                                agentId,
                                coachingResourceIds,
                            );
                        } catch (e) {
                            // Best effort; the lock will also expire on its own.
                        }
                    }
                    return false;
                }

                runInAction(() => {
                    this.hasCoachingLock = acquired;
                    this.isLockedByOther = !acquired;
                    switch (lockStatus) {
                        case CoachingNoteLockResultStatus.Acquired:
                            this.coachingLockStatus =
                                CoachingLockStatus.Acquired;
                            break;
                        case CoachingNoteLockResultStatus.AlreadyCoached:
                            this.coachingLockStatus =
                                CoachingLockStatus.AlreadyCoached;
                            break;
                        default:
                            this.coachingLockStatus =
                                CoachingLockStatus.Denied;
                            break;
                    }
                    if (acquired) {
                        this.lockedAgentId = agentId;
                        this.lockedCoachingResourceIds = coachingResourceIds;
                    }
                });

                return acquired;
            })
            .catch(() => {
                if (
                    !timedOut &&
                    requestGeneration === this.coachingLockGeneration
                ) {
                    runInAction(() => {
                        this.hasCoachingLock = false;
                        this.isLockedByOther = false;
                        this.coachingLockStatus = CoachingLockStatus.Error;
                    });
                }
                return false;
            });

        const timeoutRequest = new Promise<boolean>((resolve) => {
            timeoutHandle = setTimeout(() => {
                timedOut = true;
                if (requestGeneration === this.coachingLockGeneration) {
                    runInAction(() => {
                        this.hasCoachingLock = false;
                        this.isLockedByOther = false;
                        this.coachingLockStatus = CoachingLockStatus.Error;
                    });
                }
                resolve(false);
            }, CoachingLockTimeoutMilliseconds);
        });

        const request = Promise.race([lockRequest, timeoutRequest]).finally(
            () => {
                if (timeoutHandle) clearTimeout(timeoutHandle);
                if (this.coachingLockRequest === request) {
                    runInAction(() => {
                        this.coachingLockRequest = undefined;
                    });
                }
            },
        );

        this.coachingLockRequest = request;
        return request;
    }

    @action
    claimCoachingLockFailureNotification(): boolean {
        if (
            this.coachingLockFailureReported ||
            (this.coachingLockStatus !== CoachingLockStatus.Denied &&
                this.coachingLockStatus !==
                    CoachingLockStatus.AlreadyCoached &&
                this.coachingLockStatus !== CoachingLockStatus.Error)
        ) {
            return false;
        }

        this.coachingLockFailureReported = true;
        return true;
    }

    /**
     * Preserve a lock acquired before navigation while the destination route
     * takes ownership of the coaching session. The target is consumed by the
     * next route change so unrelated navigation still releases the lock.
     */
    @action
    prepareCoachingLockNavigation(targetLocation: string): boolean {
        if (!this.hasCoachingLock) return false;

        this.coachingLockNavigationTarget = targetLocation
            .split(/[?#]/)[0]
            .replace(/\/+$/, "")
            .toLowerCase();
        return true;
    }

    @action
    consumeCoachingLockNavigation(location: string): boolean {
        const targetLocation = this.coachingLockNavigationTarget;
        this.coachingLockNavigationTarget = undefined;
        if (!targetLocation) return false;

        const normalizedLocation = location
            .split(/[?#]/)[0]
            .replace(/\/+$/, "")
            .toLowerCase();

        return normalizedLocation === targetLocation;
    }

    /**
     * Release the coaching-notes lock if we hold one. Idempotent.
     */
    @action
    async releaseCoachingLock() {
        ++this.coachingLockGeneration;
        this.coachingLockRequest = undefined;

        const shouldRelease = this.hasCoachingLock;
        const agentId = this.lockedAgentId;
        const coachingResourceIds = this.lockedCoachingResourceIds;

        this.hasCoachingLock = false;
        this.isLockedByOther = false;
        this.coachingLockStatus = CoachingLockStatus.Idle;
        this.coachingLockFailureReported = false;
        this.lockedAgentId = undefined;
        this.lockedCoachingResourceIds = undefined;
        this.coachingLockNavigationTarget = undefined;

        if (!shouldRelease || !agentId || !coachingResourceIds) return;

        try {
            await this.agentCoachingService.releaseCoachingNoteLock(
                agentId,
                coachingResourceIds,
            );
        } catch (e) {
            // Best effort; the lock will also expire on its own.
        }
    }

    @action
    async saveAgentResponse() {
        if (!this.agentCoaching) {
            console.warn("unable to update agent coaching");
            return true;
        }
        try {
            await this.agentCoachingService.acknowledgeAgentCoaching(
                this.agentCoaching,
            );
            return true;
        } catch (e) {
            return false;
        }
    }

    @action
    reset() {
        this.agentCoaching = undefined;
        this.targetedEvaluations = undefined;
    }
}
